import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { GameState, Position, PieceType, IMove, IPiece } from "..\/..\/..\/..\/types";
import { MCTSUtils_v2 } from "..\/..\/..\/utils\/MCTSUtils";

/**
 * Priority 18: Back-up Support
 *
 * Identifies advanced pieces that need defensive support and prioritizes
 * moves that provide back-up for these "past pieces" (like past pawns in chess).
 *
 * Key concepts:
 * - Find pieces that have advanced beyond our main formation
 * - Prioritize moving pieces up to support/defend these advanced pieces
 * - Prevent advanced pieces from being isolated and captured
 * - Create chains of mutual support advancing toward the opponent
 */
export class BackupSupportPriority extends BaseDecisionPriority {
  readonly priority = 27;
  readonly name = "Back-up Support";
  readonly description = "Move pieces to defend advanced 'past pieces'";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    const gameState = context.gameState;

    // Skip if immediate tactical priorities are likely active
    if (this.isInTacticalMode(gameState)) {
      return false;
    }

    // Check if we have advanced pieces that need support
    return this.hasAdvancedPiecesNeedingSupport(gameState);
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    const gameState = context.gameState;
    const allMoves = context.allPossibleMoves;

    // Find pieces that need back-up support
    const advancedPieces = this.findAdvancedPiecesNeedingSupport(gameState);

    if (advancedPieces.length === 0) {
      return this.createNoActionResult(
        "No advanced pieces need back-up support"
      );
    }

    this.addLog(
      `🛡️ Found ${advancedPieces.length} advanced pieces needing support`
    );

    // Score moves based on how well they provide back-up support
    const scoredMoves = this.scoreMovesForBackupSupport(
      gameState,
      allMoves,
      advancedPieces
    );

    // Filter to moves that provide meaningful support (top 50% or minimum 3)
    const threshold = Math.max(3, Math.floor(scoredMoves.length * 0.5));
    const supportMoves = scoredMoves
      .filter((sm) => sm.score > 0) // Only moves that actually provide support
      .sort((a, b) => b.score - a.score)
      .slice(0, threshold)
      .map((sm) => sm.move);

    if (supportMoves.length < allMoves.length && supportMoves.length > 0) {
      this.addLog(
        `🛡️ Back-up support: Filtered to ${supportMoves.length} supporting moves`
      );
      this.addLog(`🛡️ Supporting ${advancedPieces.length} advanced pieces`);

      return this.createFilterResult(
        supportMoves,
        `Back-up support filtering - defending advanced pieces`,
        {
          logs: this.analysisLogs,
          advancedPiecesCount: advancedPieces.length,
          movesFiltered: allMoves.length - supportMoves.length,
          avgScore:
            scoredMoves.reduce((sum, sm) => sum + sm.score, 0) /
            scoredMoves.length,
        }
      );
    }

    return this.createNoActionResult(
      "No meaningful back-up support moves available"
    );
  }

  private hasAdvancedPiecesNeedingSupport(gameState: GameState): boolean {
    return this.findAdvancedPiecesNeedingSupport(gameState).length > 0;
  }

  private findAdvancedPiecesNeedingSupport(gameState: GameState): Array<{
    piece: IPiece;
    position: Position;
    supportLevel: number;
  }> {
    const ourColor = gameState.isLightTurn;
    const advancedPieces: Array<{
      piece: IPiece;
      position: Position;
      supportLevel: number;
    }> = [];

    // Find our average piece position (baseline)
    const ourPieces = this.findOurPieces(gameState);
    const averageAdvancement = this.calculateAverageAdvancement(
      ourPieces,
      ourColor
    );

    for (const pieceInfo of ourPieces) {
      // Skip the king (has its own protection priorities)
      if (pieceInfo.piece.isKing) continue;

      const advancement = this.calculateAdvancement(
        pieceInfo.position,
        ourColor
      );

      // Consider piece "advanced" if it's significantly ahead of average
      if (advancement > averageAdvancement + 1.5) {
        const supportLevel = this.calculateCurrentSupportLevel(
          gameState,
          pieceInfo.position
        );

        // Piece needs support if it has minimal support and is advanced
        if (supportLevel <= 1) {
          advancedPieces.push({
            piece: pieceInfo.piece,
            position: pieceInfo.position,
            supportLevel,
          });
        }
      }
    }

    return advancedPieces;
  }

  private scoreMovesForBackupSupport(
    gameState: GameState,
    moves: MoveInfo[],
    advancedPieces: Array<{
      piece: IPiece;
      position: Position;
      supportLevel: number;
    }>
  ): Array<{ move: MoveInfo; score: number }> {
    return moves.map((move) => ({
      move,
      score: this.calculateBackupSupportScore(gameState, move, advancedPieces),
    }));
  }

  private calculateBackupSupportScore(
    gameState: GameState,
    move: MoveInfo,
    advancedPieces: Array<{
      piece: IPiece;
      position: Position;
      supportLevel: number;
    }>
  ): number {
    let score = 0;
    const toPosition = move.move.position;
    const fromPosition = move.fromPosition;
    const movingPiece = this.getPieceAt(gameState, fromPosition);

    if (!movingPiece || movingPiece.isKing) return 0; // Don't use king for support

    // Calculate support value for each advanced piece
    for (const advancedPiece of advancedPieces) {
      const supportValue = this.calculateSupportValueForPiece(
        toPosition,
        advancedPiece.position,
        advancedPiece.piece,
        movingPiece
      );

      // Weight by how much support the piece needs
      const needFactor = Math.max(1, 3 - advancedPiece.supportLevel);
      score += supportValue * needFactor;
    }

    // Bonus for advancing while providing support
    const advancementBonus = this.calculateAdvancementValue(
      fromPosition,
      toPosition,
      gameState.isLightTurn
    );
    score += advancementBonus * 5;

    // Penalty for retreating while trying to provide support
    if (advancementBonus < 0) {
      score -= 10;
    }

    return score;
  }

  private calculateSupportValueForPiece(
    supporterPosition: Position,
    advancedPiecePosition: Position,
    advancedPiece: IPiece,
    supporterPiece: IPiece
  ): number {
    const distance = this.calculateDistance(
      supporterPosition,
      advancedPiecePosition
    );

    // Adjacent support is most valuable
    if (distance === 1) {
      return this.isValuablePiece(advancedPiece) ? 20 : 15;
    }

    // Near support is good
    if (distance === 2) {
      return this.isValuablePiece(advancedPiece) ? 12 : 8;
    }

    // Distant support has minimal value
    if (distance === 3) {
      return this.isValuablePiece(advancedPiece) ? 5 : 3;
    }

    return 0; // Too far to provide meaningful support
  }

  private calculateCurrentSupportLevel(
    gameState: GameState,
    position: Position
  ): number {
    const ourColor = gameState.isLightTurn;
    const adjacentSquares = this.getAdjacentSquares(position);

    let supportLevel = 0;
    for (const adjPos of adjacentSquares) {
      const pieces = this.getPiecesAt(gameState, adjPos);
      for (const piece of pieces) {
        if (piece.isLight === ourColor && !piece.isKing) {
          supportLevel++;
        }
      }
    }

    return supportLevel;
  }

  private findOurPieces(gameState: GameState): Array<{
    piece: IPiece;
    position: Position;
  }> {
    const ourColor = gameState.isLightTurn;
    const pieces = [];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const squarePieces = gameState.board[row][col].Pieces;
        for (const piece of squarePieces) {
          if (piece.isLight === ourColor) {
            pieces.push({ piece, position: { row, col } });
          }
        }
      }
    }

    return pieces;
  }

  private calculateAverageAdvancement(
    pieces: Array<{ piece: IPiece; position: Position }>,
    isLight: boolean
  ): number {
    if (pieces.length === 0) return 0;

    const totalAdvancement = pieces.reduce((sum, pieceInfo) => {
      return sum + this.calculateAdvancement(pieceInfo.position, isLight);
    }, 0);

    return totalAdvancement / pieces.length;
  }

  private calculateAdvancement(position: Position, isLight: boolean): number {
    // Light advances toward row 7, Dark advances toward row 0
    return isLight ? position.row : 7 - position.row;
  }

  private calculateAdvancementValue(
    fromPosition: Position,
    toPosition: Position,
    isLight: boolean
  ): number {
    const oldAdvancement = this.calculateAdvancement(fromPosition, isLight);
    const newAdvancement = this.calculateAdvancement(toPosition, isLight);

    return newAdvancement - oldAdvancement;
  }

  private isInTacticalMode(gameState: GameState): boolean {
    // Simple heuristic: if kings are close to racing, this priority is less relevant
    const ourKing = this.findOurKing(gameState);
    const opponentKing = this.findOpponentKing(gameState);

    if (!ourKing || !opponentKing) return false;

    const ourDistanceToWin = gameState.isLightTurn
      ? 7 - ourKing.position.row
      : ourKing.position.row;
    const opponentDistanceToWin = !gameState.isLightTurn
      ? 7 - opponentKing.position.row
      : opponentKing.position.row;

    // If either king is 2 moves from winning, tactical priorities take over
    return ourDistanceToWin <= 2 || opponentDistanceToWin <= 2;
  }

  private isValuablePiece(piece: IPiece): boolean {
    // Define valuable pieces that deserve extra protection
    return [
      PieceType.Bear,
      PieceType.Rhino,
      PieceType.Wolf,
      PieceType.Tree,
    ].includes(piece.type);
  }

  // Utility methods
  private getPieceAt(gameState: GameState, position: Position): IPiece | null {
    if (!this.isValidPosition(position)) return null;
    const pieces = gameState.board[position.row][position.col].Pieces;
    return pieces.length > 0 ? pieces[0] : null;
  }

  private getPiecesAt(gameState: GameState, position: Position): IPiece[] {
    if (!this.isValidPosition(position)) return [];
    return gameState.board[position.row][position.col].Pieces || [];
  }

  private getAdjacentSquares(position: Position): Position[] {
    const adjacent = [
      { row: position.row - 1, col: position.col },
      { row: position.row + 1, col: position.col },
      { row: position.row, col: position.col - 1 },
      { row: position.row, col: position.col + 1 },
    ];
    return adjacent.filter((pos) => this.isValidPosition(pos));
  }

  private calculateDistance(pos1: Position, pos2: Position): number {
    return Math.abs(pos1.row - pos2.row) + Math.abs(pos1.col - pos2.col);
  }

  private isValidPosition(position: Position): boolean {
    return (
      position.row >= 0 &&
      position.row < 8 &&
      position.col >= 0 &&
      position.col < 5
    );
  }

  private findOurKing(
    gameState: GameState
  ): { piece: IPiece; position: Position } | null {
    const ourColor = gameState.isLightTurn;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing && piece.isLight === ourColor) {
            return { piece, position: { row, col } };
          }
        }
      }
    }
    return null;
  }

  private findOpponentKing(
    gameState: GameState
  ): { piece: IPiece; position: Position } | null {
    const opponentColor = !gameState.isLightTurn;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing && piece.isLight === opponentColor) {
            return { piece, position: { row, col } };
          }
        }
      }
    }
    return null;
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }
}

